import React, { useEffect, useState } from 'react';
import { LogoSvg } from './AnimatedMightyLogo.styles';

const LOGO_ANIMATION_START_DELAY_IN_MILLISECONDS = 300;

interface AnimatedMightyLogoProps {
  isAnimated?: boolean;
  logoAnimationTransitionInMilliseconds?: number;
}

const AnimatedMightyLogo = ({
  isAnimated,
  logoAnimationTransitionInMilliseconds = LOGO_ANIMATION_START_DELAY_IN_MILLISECONDS,
}: AnimatedMightyLogoProps): JSX.Element => {
  const [isVisible, setIsVisible] = useState(false); // Only used if isAnimated is true

  useEffect(() => {
    if (!isAnimated) return;

    const timeout = setTimeout(() => {
      setIsVisible(true);
    }, logoAnimationTransitionInMilliseconds);

    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [isAnimated]);

  return (
    <LogoSvg
      className='animated-mighty-logo-container'
      isAnimated={isAnimated}
      isVisible={isVisible}
      viewBox='0 0 100 30'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        className='appear-order-0'
        d='M0 22.9655H5.10698V14.8513C5.10698 11.8739 6.20847 10.9905 7.84404 10.9905C9.41285 10.9905 10.2139 11.8739 10.2139 14.066V22.9655H15.3543V14.8513C15.3543 11.8739 16.4558 10.9905 18.058 10.9905C19.6268 10.9905 20.4613 11.9066 20.4613 14.0988V22.9655H25.6016V13.1826C25.6016 9.87805 24.5002 6.76978 19.8939 6.76978C17.524 6.76978 15.6213 7.98037 14.7535 9.68174C14.0525 8.01309 12.5839 6.76978 9.71327 6.76978C7.47688 6.76978 5.80794 7.91493 5.10698 9.48542V7.19512H0V22.9655Z'
        fill='white'
      />
      <path
        className='appear-order-1'
        d='M30.1947 6.11838C32.0306 6.11838 33.3657 4.7442 33.3657 3.07555C33.3657 1.37418 32.0306 0 30.1947 0C28.3589 0 27.0571 1.37418 27.0571 3.07555C27.0571 4.7442 28.3589 6.11838 30.1947 6.11838ZM27.6579 22.9655H32.7315V7.79001H27.6579V22.9655Z'
        fill='white'
      />
      <path
        className='appear-order-2'
        d='M46.9791 7.19512V9.45271C46.178 7.81678 44.2086 6.76978 42.0056 6.76978C37.366 6.76978 34.2951 10.2707 34.2951 14.9167C34.2951 19.5628 37.1657 22.9655 41.9389 22.9655C44.0417 22.9655 46.178 21.9839 46.9791 20.4134V22.5074C46.9791 25.3212 45.5438 26.3355 43.1739 26.3355C41.1044 26.3355 40.0029 25.583 39.7692 24.3397H34.5621C34.6289 26.041 35.7971 27.7424 37.4327 28.724C39.0015 29.6401 40.9375 30 43.2406 30C45.5438 30 47.5131 29.6074 48.9818 28.5931C50.9511 27.2516 52.0527 24.9941 52.0861 21.9512V7.19512H46.9791ZM43.3408 19.1047C41.0376 19.1047 39.5356 17.3706 39.5356 14.9822C39.5356 12.5937 41.0376 10.8596 43.3408 10.8596C45.7774 10.8596 47.2127 12.6918 47.2127 14.9822C47.2127 17.2725 45.7774 19.1047 43.3408 19.1047Z'
        fill='white'
      />
      <path
        className='appear-order-3'
        d='M64.4825 6.76978C62.0459 6.76978 60.21 7.71862 59.2754 9.48542V0.0624621H54.1685V22.9655H59.2754V14.7204C59.2754 12.2992 60.3436 10.9905 62.3797 10.9905C64.4492 10.9905 65.2169 12.2011 65.2169 14.8513V22.9655H70.3573V13.2154C70.3573 8.92921 68.3879 6.76978 64.4825 6.76978Z'
        fill='white'
      />
      <path
        className='appear-order-4'
        d='M79.7712 19.072C78.5028 19.072 77.5348 18.7121 77.5348 16.3891V11.1541H81.5403V7.19512H77.5348V2.51636H72.4278V17.4361C72.4278 22.7037 75.7324 23.2272 78.1357 23.2272C79.9688 23.2272 81.9957 22.7424 83.1336 22.3825C83.1336 22.3825 82.537 21.2671 82.3094 20.6722C82.0818 20.0773 81.7681 18.9619 81.7681 18.9619C81.3008 19.0274 80.3387 19.072 79.7712 19.072Z'
        fill='white'
      />
      <path
        className='appear-order-5'
        d='M94.4443 7.19512V15.0149C94.4443 17.4361 93.4095 19.0065 91.34 19.0065C89.2038 19.0065 88.5028 17.6324 88.5028 15.6038V7.19512H83.3958V16.3563C83.3958 20.2826 85.2317 22.9655 89.3373 22.9655C91.7739 22.9655 93.543 22.0167 94.4443 20.348V22.8019C94.4443 25.2558 93.2093 26.3355 91.34 26.3355C89.404 26.3355 88.6363 25.5503 88.5362 24.2088H83.3958C83.4292 25.9102 84.2303 27.3825 85.3986 28.3641C86.7337 29.4438 88.603 30 91.34 30C94.0437 30 95.8128 29.3783 97.2481 28.1678C98.6166 26.9572 99.5512 25.0268 99.5512 22.4747V7.19512H94.4443Z'
        fill='white'
      />
    </LogoSvg>
  );
};

export default AnimatedMightyLogo;
